const getConsent = (showUrl: string): Promise<any> => fetch(showUrl, {
  headers: {
    Accept: 'application/json',
  },
}).then((response) => response.json());

const getSessionInfo = (): Promise<any> => fetch('/actions/users/session-info/', {
  headers: {
    Accept: 'application/json',
  },
}).then((response) => response.json());

/*!
 * Serialize all form data into a query string
 * (c) 2018 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param  {Node}   form The form to serialize
 * @return {String}      The serialized form data
 */
const serialize = (form: HTMLFormElement): string => {
  const serialized: string[] = [];
  const elements = Array.from(form.elements) as (HTMLInputElement | HTMLSelectElement)[];
  for (let i = 0; i < elements.length; i += 1) {
    const field = elements[i];
    const isSkipField = !field.name
      || field.disabled
      || field.type === 'file'
      || field.type === 'reset'
      || field.type === 'button';
    if (isSkipField) {
      // skip
    } else if (field instanceof HTMLSelectElement && field.type === 'select-multiple') {
      for (let n = 0; n < field.options.length; n += 1) {
        if (field.options[n].selected) {
          serialized.push(
            `${encodeURIComponent(field.name)}=${encodeURIComponent(field.options[n].value)}`,
          );
        }
      }
    } else if (
      (field.type !== 'checkbox' && field.type !== 'radio')
      || (field).checked
    ) {
      serialized.push(
        `${encodeURIComponent(field.name)}=${encodeURIComponent(field.value)}`,
      );
    }
  }
  return serialized.join('&');
};

const setCookie = (name: string, value: string, days: number): void => {
  const d = new Date();
  d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = `expires=${d.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/;SameSite=Strict;`;
};

const setGclId = (): void => {
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.has('gclid')) {
    setCookie('gclid', searchParams.get('gclid') ?? '', 2);
  }
};

const runTracking = (): void => {
  if ((window as any).trackingRan || typeof (window as any).dataLayer === 'undefined') {
    return;
  }
  const { ccc } = (window as any);
  if (ccc !== null && ccc.necessary === true) {
    (window as any).dataLayer.push({ event: 'cookieconsent_necessary' });
  }
  if (ccc !== null && ccc.functional === true) {
    (window as any).dataLayer.push({ event: 'cookieconsent_functional' });
  }
  if (ccc !== null && ccc.analytical === true) {
    (window as any).dataLayer.push({ event: 'cookieconsent_analytical' });
  }
  if (ccc !== null && ccc.targeting === true) {
    setGclId();
    (window as any).dataLayer.push({ event: 'cookieconsent_targeting' });
  }
  (window as any).trackingRan = true;
};

const refresh = (ms: number): void => {
  setTimeout(() => {
    window.location.reload();
  }, ms);
};

const addWindowObject = (consent: any): void => {
  if (consent === 'null' || consent === null) {
    (window as any).ccc = {};
  } else {
    (window as any).ccc = consent;
    runTracking();
  }
};

export default function initCookieConsent() {
  Alpine.data('cookieConsent', () => ({
    consent: null as any,
    dialog: null as unknown as HTMLDialogElement,
    showCookieBar: false,
    showGroups: false,
    groupsAreHidden: true,

    init() {
      const form = this.$refs.consentForm;
      this.dialog = this.$refs.consentDialog as HTMLDialogElement;
      getSessionInfo().then((session: any) => {
        const csrfInput = this.$refs.csrfToken as HTMLInputElement;
        csrfInput.value = session.csrfTokenValue;
      });
      getConsent(form.dataset.show ?? '').then((consent: any) => {
        this.consent = consent;
        if (this.consent == null) {
          this.showCookieBar = true;
          if (this.dialog) {
            this.dialog.show();
          }
        } else {
          const necessaryCheck = this.$refs.necessary as HTMLInputElement;
          if (necessaryCheck && !necessaryCheck.disabled) {
            necessaryCheck.checked = consent.necessary;
          }
          const functionalCheck = this.$refs.functional as HTMLInputElement;
          if (functionalCheck && !functionalCheck.disabled) {
            functionalCheck.checked = consent.functional;
          }
          const analyticalCheck = this.$refs.analytical as HTMLInputElement;
          if (analyticalCheck && !analyticalCheck.disabled) {
            analyticalCheck.checked = consent.analytical;
          }
          const targetingCheck = this.$refs.targeting as HTMLInputElement;
          if (targetingCheck && !targetingCheck.disabled) {
            targetingCheck.checked = consent.targeting;
          }
        }
        addWindowObject(this.consent);
      });
      this.$watch('showCookieBar', () => {
        if (this.showCookieBar) {
          const acceptBtn = document.querySelector('button[data-action="accept"]');
          if (acceptBtn) {
            setTimeout(() => {
              (acceptBtn as HTMLButtonElement).focus();
            }, 10);
          }
        }
      });
    },

    toggleGroups() {
      this.showGroups = !this.showGroups;
      this.groupsAreHidden = !this.groupsAreHidden;
    },

    handleConsentSubmit(e: SubmitEvent) {
      e.preventDefault();
      this.showCookieBar = false;
      if (this.dialog) {
        this.dialog.close();
      }
      const submitBtn = e.submitter;
      const form = this.$refs.consentForm as HTMLFormElement;
      let data = serialize(form);
      const checkboxes = form.querySelectorAll<HTMLInputElement>('input[type="checkbox"]');
      function setCheckboxChecked(checkbox: HTMLInputElement, value: boolean) {
        // eslint-disable-next-line no-param-reassign
        checkbox.checked = value;
      }
      if (submitBtn?.dataset.action === 'accept') {
        data = data.concat('&acceptAll=true');
        checkboxes.forEach((checkbox) => {
          if (!checkbox.checked) {
            setCheckboxChecked(checkbox, true);
          }
        });
      } else if (submitBtn?.dataset.action === 'decline') {
        checkboxes.forEach((checkbox) => {
          if (checkbox.checked && !checkbox.disabled) {
            const checkboxName = encodeURIComponent(checkbox.name);
            const regex = new RegExp(`([&?]${checkboxName}=)on\\b`);
            data = data.replace(regex, '$1off');
            setCheckboxChecked(checkbox, false);
          }
        });
      }
      const xhr = new XMLHttpRequest();
      xhr.open('POST', form.dataset.url ?? '');
      xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
      xhr.send(data);
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          getConsent(form.dataset.show ?? '').then((consent) => {
            this.consent = consent;
            addWindowObject(this.consent);
            if (this.$refs.successNotification) {
              this.$refs.successNotification.classList.remove('hidden');
              setTimeout(() => {
                this.$refs.successNotification.classList.add('hidden');
              }, 4000);
            }
          });
          if (xhr.status !== 200) {
            window.dispatchEvent(new CustomEvent('cookieConsentError', { detail: xhr.status }));
          } else if (
            form.hasAttribute('data-refresh')
            && form.dataset.refresh === 'true'
            && form.hasAttribute('data-refreshtime')
          ) {
            refresh(parseInt(form.dataset.refreshtime ?? '2000', 10));
          }
        }
      };
    },
  }));
}